/**
 * URL Content Extractor
 * Витягує структуровані дані зі сторінки для AI генерації
 * Використовує той же механізм що й editor-parser для стабільності
 */

import { generateAIRecommendations } from './openai';

export interface ExtractedPageData {
  title: string;
  contentType: 'product' | 'category' | 'article' | 'service' | 'other';
  
  // Для продуктів/категорій
  prices: string[];
  brands: string[];
  products: string[];
  
  // Для всіх типів контенту
  headers: {
    h1: string[];
    h2: string[];
    h3: string[];
  };
  benefits: string[];
  features: string[];
  specifications: Record<string, string>;
  
  // Саммарі та основний контент
  summary: string;
  mainContent: string;
  fullText: string;
  wordCount: number;
  
  // AI аналіз сторінки
  aiAnalysis?: {
    pageType: string;
    mainTopic: string;
    keyProducts: string[];
    keyServices: string[];
    priceList?: string[];  // Ціни з контекстом (сервіс: ціна)
    uniqueApproach?: string[];  // Унікальні підходи/методології
    certificates?: string[];  // Сертифікати, ліцензії, нагороди
    competitiveAdvantages?: string[];  // Конкурентні переваги
    specificNumbers?: string[];  // Конкретні цифри (роки, клієнти, %)
    importantFacts: string[];
    recommendations: string;
  };
}

/**
 * Fetch HTML через CORS proxies (той же механізм що й в editor-parser)
 */
async function fetchWithRetry(url: string, maxRetries = 3): Promise<string> {
  const proxyUrls = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://corsproxy.io/?${encodeURIComponent(url)}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
  ];

  let lastError;
  for (let retry = 0; retry < maxRetries; retry++) {
    for (const proxyUrl of proxyUrls) {
      try {
        console.log('🔗 Trying proxy:', proxyUrl);
        
        const response = await fetch(proxyUrl, {
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          },
          signal: AbortSignal.timeout(10000)
        });
        
        if (!response.ok) {
          console.warn(`Proxy ${proxyUrl} returned status ${response.status}`);
          continue;
        }
        
        const html = await response.text();
        
        if (html.length > 500 && 
            (html.includes('</html>') || html.includes('</body>'))) {
          console.log('✅ Successfully fetched content with length:', html.length);
          return html;
        }
        
        console.warn(`Proxy ${proxyUrl} returned invalid content length: ${html.length}`);
      } catch (error) {
        lastError = error;
        console.warn(`Proxy ${proxyUrl} failed:`, error);
        continue;
      }
    }
    
    // Затримка перед повтором
    await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
  }

  throw lastError || new Error('Failed to fetch content after all retries');
}

/**
 * Очищує текст від зайвих символів
 */
function cleanText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s\-.,?!()«»""''€$₴₽]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Визначає тип контенту на основі вмісту сторінки
 */
function detectContentType(doc: Document): 'product' | 'category' | 'article' | 'service' | 'other' {
  const html = doc.body.innerHTML.toLowerCase();
  const text = doc.body.textContent?.toLowerCase() || '';
  
  // Продукт - якщо є чітка ціна та кнопка купити
  const hasPrice = /(\d[\d\s]*[.,]?\d*)\s*(грн|uah|₴|руб|rub|₽|usd|\$|eur|€)/i.test(text);
  const hasBuyButton = /купить|buy|add to cart|в корзин|купити|замовити|заказать/.test(text);
  const hasProductSchema = html.includes('"@type":"product"') || html.includes('itemprop="product"');
  
  if ((hasPrice && hasBuyButton) || hasProductSchema) {
    return 'product';
  }
  
  // Категорія - якщо багато товарів/карточок
  const hasProductCards = (html.match(/class="[^"]*product[^"]*"/g) || []).length > 3;
  const hasProductList = (html.match(/class="[^"]*item[^"]*"/g) || []).length > 5;
  const hasCategorySchema = html.includes('"@type":"itemlist"') || html.includes('"@type":"collectionpage"');
  
  if (hasProductCards || hasProductList || hasCategorySchema) {
    return 'category';
  }
  
  // Стаття - якщо є дата публікації та довгий текст
  const hasArticleDate = /published|дата|опубліковано|опубликовано/.test(html);
  const hasArticleSchema = html.includes('"@type":"article"') || html.includes('"@type":"blogposting"');
  const hasLongContent = text.length > 2000;
  
  if ((hasArticleDate && hasLongContent) || hasArticleSchema) {
    return 'article';
  }
  
  // Послуга - якщо є форма зворотнього зв'язку та опис послуг
  const hasContactForm = html.includes('type="email"') && (html.includes('submit') || html.includes('відправити'));
  const hasServiceKeywords = /послуг|услуг|service|консультац|consultation/.test(text);
  
  if (hasContactForm && hasServiceKeywords) {
    return 'service';
  }
  
  return 'other';
}

/**
 * Витягує заголовки різних рівнів
 */
function extractHeaders(doc: Document): { h1: string[]; h2: string[]; h3: string[] } {
  const headers = {
    h1: [] as string[],
    h2: [] as string[],
    h3: [] as string[]
  };
  
  ['h1', 'h2', 'h3'].forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => {
      const text = cleanText(el.textContent || '');
      if (text.length >= 3 && text.length < 200) {
        headers[tag as keyof typeof headers].push(text);
      }
    });
  });
  
  return headers;
}

/**
 * Витягує ціни зі сторінки з контекстом (за що ціна)
 */
function extractPrices(doc: Document): string[] {
  const pricesWithContext: string[] = [];
  const pricePatterns = [
    /(\d[\d\s]*[.,]?\d*)\s*(грн|UAH|₴|руб|RUB|₽|USD|\$|EUR|€)/gi,
    /(\d{1,3}(?:[,\s]\d{3})*(?:[.,]\d{2})?)\s*(грн|UAH|₴)/gi
  ];

  // Шукаємо в елементах з класами/атрибутами про ціни
  const priceSelectors = [
    '[class*="price"]',
    '[data-price]',
    '[itemprop="price"]',
    '[class*="cost"]',
    '[class*="amount"]',
    'td', // таблиці часто містять ціни
    'li', // списки послуг з цінами
    'p'   // параграфи з цінами
  ];

  priceSelectors.forEach(selector => {
    doc.querySelectorAll(selector).forEach(el => {
      const text = cleanText(el.textContent || '');
      if (text.length > 5 && text.length < 300) {
        pricePatterns.forEach(pattern => {
          const match = text.match(pattern);
          if (match) {
            // Витягуємо контекст - текст до ціни
            const priceIndex = text.indexOf(match[0]);
            const contextBefore = text.substring(0, priceIndex).trim();
            
            // Якщо є контекст перед ціною (назва послуги/товару)
            if (contextBefore.length > 3) {
              // Беремо останні 50 символів перед ціною як контекст
              const context = contextBefore.slice(-80).trim();
              const priceWithContext = `${context}: ${match[0]}`;
              pricesWithContext.push(priceWithContext);
            } else {
              // Якщо контексту немає, шукаємо в батьківських елементах
              let parent = el.parentElement;
              let attempts = 0;
              while (parent && attempts < 3) {
                const parentText = cleanText(parent.textContent || '');
                if (parentText.length > text.length && parentText.length < 500) {
                  // Знайшли батьківський елемент з більшим текстом
                  const priceIndex = parentText.indexOf(match[0]);
                  if (priceIndex > 0) {
                    const contextBefore = parentText.substring(0, priceIndex).trim();
                    const context = contextBefore.slice(-80).trim();
                    if (context.length > 3) {
                      pricesWithContext.push(`${context}: ${match[0]}`);
                      break;
                    }
                  }
                }
                parent = parent.parentElement;
                attempts++;
              }
              
              // Якщо так і не знайшли контекст, додаємо просто ціну
              if (attempts >= 3 && !pricesWithContext.some(p => p.includes(match[0]))) {
                pricesWithContext.push(match[0]);
              }
            }
          }
        });
      }
    });
  });

  // Унікальні значення
  return [...new Set(pricesWithContext)].slice(0, 15);
}

/**
 * Витягує бренди/назви продуктів
 */
function extractBrands(doc: Document): string[] {
  const brands: string[] = [];
  
  const brandSelectors = [
    '[itemprop="brand"]',
    '[class*="brand"]',
    '[data-brand]',
    'h1'
  ];

  brandSelectors.forEach(selector => {
    doc.querySelectorAll(selector).forEach(el => {
      const text = cleanText(el.textContent || '');
      if (text && text.length < 100) {
        brands.push(text);
      }
    });
  });

  return [...new Set(brands)].slice(0, 10);
}

/**
 * Витягує список продуктів зі сторінки категорії
 */
function extractProducts(doc: Document): string[] {
  const products: string[] = [];
  
  // Шукаємо карточки товарів
  const productSelectors = [
    '.product-card h3',
    '.product-card h2',
    '.product-item h3',
    '.product-item h2',
    '[class*="product"] h3',
    '[class*="product"] h2',
    '[class*="item-title"]',
    '[itemprop="name"]'
  ];

  productSelectors.forEach(selector => {
    doc.querySelectorAll(selector).forEach(el => {
      const text = cleanText(el.textContent || '');
      if (text && text.length > 5 && text.length < 150) {
        products.push(text);
      }
    });
  });

  return [...new Set(products)].slice(0, 20);
}

/**
 * Витягує переваги/особливості
 */
function extractBenefits(doc: Document): string[] {
  const benefits: string[] = [];
  
  // Шукаємо списки
  doc.querySelectorAll('ul li, ol li').forEach(li => {
    const text = cleanText(li.textContent || '');
    if (text && text.length > 10 && text.length < 200) {
      benefits.push(text);
    }
  });

  return [...new Set(benefits)].slice(0, 20);
}

/**
 * Витягує характеристики/специфікації
 */
function extractSpecifications(doc: Document): Record<string, string> {
  const specs: Record<string, string> = {};
  
  // Шукаємо таблиці характеристик
  doc.querySelectorAll('table').forEach(table => {
    table.querySelectorAll('tr').forEach(row => {
      const cells = row.querySelectorAll('td, th');
      if (cells.length >= 2) {
        const key = cleanText(cells[0].textContent || '');
        const value = cleanText(cells[1].textContent || '');
        if (key && value && key.length < 100 && value.length < 200) {
          specs[key] = value;
        }
      }
    });
  });

  // Шукаємо в dl (definition lists)
  doc.querySelectorAll('dl').forEach(dl => {
    const dts = dl.querySelectorAll('dt');
    const dds = dl.querySelectorAll('dd');
    dts.forEach((dt, index) => {
      if (dds[index]) {
        const key = cleanText(dt.textContent || '');
        const value = cleanText(dds[index].textContent || '');
        if (key && value) {
          specs[key] = value;
        }
      }
    });
  });

  // Обмежуємо кількість
  const entries = Object.entries(specs).slice(0, 20);
  return Object.fromEntries(entries);
}

/**
 * Витягує основний текстовий контент
 */
function extractFullText(doc: Document): string {
  const texts: string[] = [];
  
  // Видаляємо зайві елементи
  const unwantedSelectors = [
    'script', 'style', 'noscript', 'iframe', 'nav', 'footer',
    'header', 'aside', '.menu', '.navigation', '.sidebar',
    '.cookie', '.popup', '.modal', 'form', 'button'
  ];
  
  const cleanDoc = doc.cloneNode(true) as Document;
  unwantedSelectors.forEach(selector => {
    cleanDoc.querySelectorAll(selector).forEach(el => el.remove());
  });
  
  // Витягуємо параграфи
  cleanDoc.querySelectorAll('p').forEach(p => {
    const text = cleanText(p.textContent || '');
    if (text.length >= 20) {
      texts.push(text);
    }
  });
  
  return texts.join('\n\n');
}

/**
 * Аналізує сторінку через AI для отримання структурованого саммарі
 */
async function analyzePageWithAI(
  title: string,
  fullText: string,
  headers: { h1: string[]; h2: string[]; h3: string[] },
  prices: string[],
  brands: string[],
  products: string[]
): Promise<ExtractedPageData['aiAnalysis']> {
  try {
    console.log('🤖 Starting AI analysis of page content...');
    
    // Збільшуємо текст до 8000 символів для кращого аналізу
    const textForAnalysis = fullText.slice(0, 8000);
    
    const analysisPrompt = `Extract UNIQUE and SPECIFIC information from this page that makes it stand out from generic content.

PAGE TITLE: ${title}

HEADERS:
${[...headers.h1, ...headers.h2, ...headers.h3].slice(0, 20).join('\n')}

${prices.length > 0 ? `PRICES FOUND (with context): 
${prices.slice(0, 10).join('\n')}` : ''}
${brands.length > 0 ? `BRANDS: ${brands.slice(0, 5).join(', ')}` : ''}
${products.length > 0 ? `PRODUCTS: ${products.slice(0, 10).join(', ')}` : ''}

MAIN CONTENT:
${textForAnalysis}

---

🎯 YOUR TASK: Extract ONLY unique, specific, and valuable information that differentiates THIS company/service from others.

Focus on extracting:
1. SPECIFIC PRICES with exact services (not ranges, exact numbers with what they include)
2. UNIQUE APPROACHES or methodologies this company uses
3. CERTIFICATES, awards, accreditations (with specific names and dates)
4. COMPETITIVE ADVANTAGES that are unique to this company
5. SPECIFIC NUMBERS: years in business, number of clients, success rates, guarantees
6. SPECIAL PROGRAMS or packages that are unique
7. EQUIPMENT or technology mentioned by name/model
8. TEAM QUALIFICATIONS with specific credentials
9. GUARANTEES or warranties with specific terms
10. REAL CASE RESULTS with specific numbers

❌ DO NOT extract:
- Generic industry information
- Common benefits that any company could claim
- Vague statements without specifics
- General process descriptions

Provide a JSON response with this EXACT structure (all fields are REQUIRED):
{
  "pageType": "service",
  "mainTopic": "What SPECIFICALLY makes this company/service unique (1-2 sentences with concrete details)",
  "keyProducts": ["specific product/service names with their unique features - at least 2"],
  "keyServices": ["services with what makes them unique or different - at least 2"],
  "priceList": ["EXACT prices with FULL context: 'Базовий пакет консультація: 77450 грн', 'Розширений пакет: 110150 грн' - extract ALL prices shown above"],
  "uniqueApproach": ["specific methodologies, techniques, or approaches - at least 1-2 items"],
  "certificates": ["specific certificates, licenses, awards with names - look for license numbers, accreditations"],
  "competitiveAdvantages": ["concrete advantages with numbers or specifics - '15 років досвіду', 'власна лабораторія' - at least 2-3"],
  "specificNumbers": ["15 років на ринку", "2500+ клієнтів", "успішність 68%" - extract ALL numbers mentioned"],
  "importantFacts": ["5-10 SPECIFIC facts with numbers, names, dates - 'Використовують обладнання Olympus 2023', 'Член асоціації ESHRE з 2018'"],
  "recommendations": "What UNIQUE content elements must be used to make generated content stand out"
}

🚨 MANDATORY - FILL ALL FIELDS:
- If field is empty array [] - you FAILED to extract properly
- priceList: MUST contain ALL prices from "PRICES FOUND" section above with full service description
- uniqueApproach: MUST have at least 1 item (look for methodology, process, approach)
- certificates: MUST look for license numbers, memberships, awards in content
- competitiveAdvantages: MUST have at least 2 items (years in business counts, own equipment counts)
- specificNumbers: MUST extract ALL numbers (years, percentages, quantities)
- importantFacts: MINIMUM 5 items with specific details

⚠️ If you return empty arrays [] for most fields - your analysis is REJECTED!

🚨 CRITICAL REQUIREMENTS:
- Extract ONLY information with SPECIFIC details (numbers, names, dates)
- If price is shown - include FULL SERVICE DESCRIPTION with it
- Focus on DIFFERENTIATORS, not generic features
- Include specific credentials, not just "experienced team"
- Extract measurable claims (5 years, 1000+ clients, 95% success rate)
- Ignore vague marketing language without specifics

Return ONLY valid JSON, no markdown formatting.`;

    const aiResponse = await generateAIRecommendations(analysisPrompt);
    
    console.log('🤖 AI raw response:', aiResponse);
    
    // Очищаємо відповідь від markdown форматування якщо є
    let cleanedResponse = aiResponse.trim();
    if (cleanedResponse.startsWith('```json')) {
      cleanedResponse = cleanedResponse.replace(/^```json\s*/, '').replace(/```\s*$/, '');
    } else if (cleanedResponse.startsWith('```')) {
      cleanedResponse = cleanedResponse.replace(/^```\s*/, '').replace(/```\s*$/, '');
    }
    
    const analysis = JSON.parse(cleanedResponse);
    
    console.log('✅ AI analysis completed:', analysis);
    console.log('📊 Fields extracted:', {
      uniqueApproach: analysis.uniqueApproach?.length || 0,
      certificates: analysis.certificates?.length || 0,
      priceList: analysis.priceList?.length || 0,
      competitiveAdvantages: analysis.competitiveAdvantages?.length || 0,
      specificNumbers: analysis.specificNumbers?.length || 0,
      importantFacts: analysis.importantFacts?.length || 0
    });
    
    return analysis;
    
  } catch (error) {
    console.error('❌ AI analysis failed:', error);
    return undefined;
  }
}

/**
 * Створює короткий саммарі сторінки
 */
function createSummary(doc: Document, fullText: string, maxLength: number = 800): string {
  // Пробуємо знайти мета-опис
  const metaDesc = doc.querySelector('meta[name="description"]')?.getAttribute('content');
  if (metaDesc && metaDesc.length > 50) {
    return cleanText(metaDesc);
  }

  // Якщо мета-опису немає, береме початок основного тексту
  if (fullText.length > 0) {
    const sentences = fullText.split(/[.!?]\s+/).filter(s => s.length > 20);
    const summary = sentences.slice(0, 3).join('. ');
    return summary.slice(0, maxLength) + (summary.length > maxLength ? '...' : '.');
  }

  return '';
}

/**
 * Основна функція витягування даних з URL
 */
export async function extractPageData(url: string): Promise<ExtractedPageData | null> {
  try {
    console.log('🔍 Extracting data from URL:', url);

    // Використовуємо той же механізм що й editor-parser
    const html = await fetchWithRetry(url);
    
    // Парсимо HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Видаляємо скрипти та стилі
    doc.querySelectorAll('script, style, noscript').forEach(el => el.remove());

    // Витягуємо title
    const title = cleanText(
      doc.querySelector('title')?.textContent || 
      doc.querySelector('h1')?.textContent || 
      'Без назви'
    );

    // Визначаємо тип контенту
    const contentType = detectContentType(doc);

    // Витягуємо всі дані
    const headers = extractHeaders(doc);
    const prices = extractPrices(doc);
    const brands = extractBrands(doc);
    const products = extractProducts(doc);
    const benefits = extractBenefits(doc);
    const specifications = extractSpecifications(doc);
    const fullText = extractFullText(doc);
    const summary = createSummary(doc, fullText);

    // Основний контент (перші кілька параграфів)
    const mainContent = fullText.split('\n\n').slice(0, 5).join('\n\n');

    // Підраховуємо слова
    const wordCount = fullText.split(/\s+/).filter(Boolean).length;

    console.log('📊 Basic extraction completed, starting AI analysis...');
    
    // Аналізуємо сторінку через AI
    const aiAnalysis = await analyzePageWithAI(title, fullText, headers, prices, brands, products);

    const result: ExtractedPageData = {
      title,
      contentType,
      prices,
      brands,
      products,
      headers,
      benefits,
      features: benefits, // features = benefits
      specifications,
      summary,
      mainContent,
      fullText: fullText.slice(0, 3000), // Обмежуємо довжину
      wordCount,
      aiAnalysis
    };

    console.log('✅ Extracted page data:', {
      title: result.title,
      contentType: result.contentType,
      pricesCount: result.prices.length,
      brandsCount: result.brands.length,
      productsCount: result.products.length,
      headersCount: Object.values(result.headers).flat().length,
      benefitsCount: result.benefits.length,
      specsCount: Object.keys(result.specifications).length,
      wordCount: result.wordCount,
      hasAiAnalysis: !!result.aiAnalysis
    });
    
    return result;

  } catch (error) {
    console.error('❌ Error extracting page data:', error);
    return null;
  }
}

/**
 * Форматує витягнуті дані для промпту
 */
export function formatExtractedDataForPrompt(data: ExtractedPageData): string {
  const parts: string[] = [];

  // AI Аналіз (найважливіше - на початку!)
  if (data.aiAnalysis) {
    parts.push(`**🤖 AI PAGE ANALYSIS - UNIQUE INFORMATION:**`);
    parts.push(`Page Type: ${data.aiAnalysis.pageType}`);
    parts.push(`Main Topic: ${data.aiAnalysis.mainTopic}`);
    
    // 🎯 Унікальні підходи та методології
    if (data.aiAnalysis.uniqueApproach && data.aiAnalysis.uniqueApproach.length > 0) {
      parts.push(`\n🎯 UNIQUE APPROACH/METHODOLOGY (use this to differentiate!):\n${data.aiAnalysis.uniqueApproach.map(a => `- ${a}`).join('\n')}`);
    }
    
    // 🏆 Сертифікати та нагороди
    if (data.aiAnalysis.certificates && data.aiAnalysis.certificates.length > 0) {
      parts.push(`\n🏆 CERTIFICATES/AWARDS/LICENSES (mention these for credibility!):\n${data.aiAnalysis.certificates.map(c => `- ${c}`).join('\n')}`);
    }
    
    // 💰 Ціни з ПОВНИМ контекстом
    if (data.aiAnalysis.priceList && data.aiAnalysis.priceList.length > 0) {
      parts.push(`\n💰 EXACT PRICES WITH FULL SERVICE DESCRIPTIONS:\n${data.aiAnalysis.priceList.map(p => `- ${p}`).join('\n')}`);
    }
    
    // 🚀 Конкурентні переваги
    if (data.aiAnalysis.competitiveAdvantages && data.aiAnalysis.competitiveAdvantages.length > 0) {
      parts.push(`\n🚀 COMPETITIVE ADVANTAGES (highlight these!):\n${data.aiAnalysis.competitiveAdvantages.map(a => `- ${a}`).join('\n')}`);
    }
    
    // 📊 Конкретні цифри
    if (data.aiAnalysis.specificNumbers && data.aiAnalysis.specificNumbers.length > 0) {
      parts.push(`\n📊 SPECIFIC NUMBERS & METRICS (use for credibility!):\n${data.aiAnalysis.specificNumbers.map(n => `- ${n}`).join('\n')}`);
    }
    
    if (data.aiAnalysis.keyProducts.length > 0) {
      parts.push(`\nKey Products/Services:\n${data.aiAnalysis.keyProducts.map(p => `- ${p}`).join('\n')}`);
    }
    
    if (data.aiAnalysis.importantFacts.length > 0) {
      parts.push(`\n⭐ IMPORTANT SPECIFIC FACTS (must use!):\n${data.aiAnalysis.importantFacts.map(f => `- ${f}`).join('\n')}`);
    }
    
    if (data.aiAnalysis.recommendations) {
      parts.push(`\n💡 Content Strategy:\n${data.aiAnalysis.recommendations}`);
    }
    
    parts.push(`\n${'='.repeat(60)}\n`);
  }

  // Тип контенту
  parts.push(`**Content Type:** ${data.contentType}`);

  // Заголовок
  if (data.title) {
    parts.push(`**Page Title:** ${data.title}`);
  }

  // Саммарі
  if (data.summary) {
    parts.push(`**Summary:** ${data.summary}`);
  }

  // Структура заголовків
  const allHeaders = [...data.headers.h1, ...data.headers.h2, ...data.headers.h3];
  if (allHeaders.length > 0) {
    parts.push(`**Page Headers:**\n${allHeaders.slice(0, 10).map(h => `- ${h}`).join('\n')}`);
  }

  // Для продуктів/категорій - ціни та бренди
  if (data.prices.length > 0) {
    parts.push(`**Prices Found:** ${data.prices.slice(0, 5).join(', ')}`);
  }

  if (data.brands.length > 0) {
    parts.push(`**Brands/Titles:** ${data.brands.slice(0, 5).join(', ')}`);
  }

  // Для категорій - список продуктів
  if (data.products.length > 0) {
    parts.push(`**Products on Page:**\n${data.products.slice(0, 10).map(p => `- ${p}`).join('\n')}`);
  }

  // Переваги/особливості
  if (data.benefits.length > 0) {
    parts.push(`**Key Points/Benefits:**\n${data.benefits.slice(0, 10).map(b => `- ${b}`).join('\n')}`);
  }

  // Характеристики
  if (Object.keys(data.specifications).length > 0) {
    const specEntries = Object.entries(data.specifications).slice(0, 10);
    parts.push(`**Specifications:**\n${specEntries.map(([k, v]) => `- ${k}: ${v}`).join('\n')}`);
  }

  // Основний контент (якщо немає специфічних даних)
  if (data.mainContent && parts.length < 5) {
    parts.push(`**Main Content Preview:**\n${data.mainContent.slice(0, 500)}...`);
  }

  // Статистика
  parts.push(`**Content Statistics:** ${data.wordCount} words total`);

  return parts.join('\n\n');
}
